// The automation builder (HQ): trigger → who enters → branches → steps → stop rules → re-entry. Everything offered comes
// from the server's catalog (/automation-engine/catalog), so a new trigger, condition field or step type shows
// up here without a frontend change. The draft is checked by the server on save; publishing freezes it as a
// version, and customers already in it keep the version they started with.
import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import api from '../services/api';
import { useEngagementScope } from '../context/EngagementScope';
import BranchSelect from './BranchSelect';
import { Modal, Spinner } from './ui';

const UNITS = ['minutes', 'hours', 'days'];
const LANGUAGE_CHOICES = [
  ['customer', "Customer's language"],
  ['en', 'English'],
  ['ta', 'Tamil'],
  ['hi', 'Hindi'],
  ['te', 'Telugu'],
  ['kn', 'Kannada'],
  ['ml', 'Malayalam'],
];
const STEP_ICON = {
  wait: '⏱',
  wait_by_service: '📆',
  wait_until: '🕒',
  wait_for: '💬',
  send_template: '📨',
  send_text: '✉️',
  condition: '🔀',
  tag: '🏷',
  notify_staff: '🔔',
  handoff: '🙋',
  stop_automations: '⛔',
  start_automation: '▶️',
  repeat: '🔁',
  pick_offer: '🎁',
  save_answer: '💾',
  offer_booking: '📅',
  end: '🏁',
};
const REASON_LABEL = { price: 'Price', time: 'Time', changed_mind: 'Changed my mind', other: 'Other' };
const TEMPLATE_STATUS = { approved: 'approved', pending: 'waiting for Meta', rejected: 'rejected', draft: 'not submitted', paused: 'paused', disabled: 'disabled' };

function listText(value) {
  return Array.isArray(value) ? value.join(', ') : '';
}

function parseList(text) {
  return String(text || '')
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function newKey(steps, type) {
  const base = type.replace(/[^a-z]/g, '').slice(0, 8) || 'step';
  let n = 1;
  while (steps.some((s) => s.key === `${base}${n}`)) n += 1;
  return `${base}${n}`;
}

function defaultConfig(type) {
  switch (type) {
    case 'wait':
      return { amount: 1, unit: 'days' };
    case 'wait_by_service':
      return { rules: [], default_days: 25 };
    case 'wait_until':
      return { relative_to: 'appointment', offset_minutes: -180, if_past: 'skip_next' };
    case 'wait_for':
      return { events: { 'button:0': 'end' }, timeout_minutes: 1440, timeout_next: 'end' };
    case 'send_template':
      return { template: '', language: 'customer' };
    case 'send_text':
      return { text: '', buttons: [] };
    case 'condition':
      return { conditions: { all: [] }, yes: '', no: 'end' };
    case 'tag':
      return { add: [], remove: [] };
    case 'notify_staff':
      return { message: '' };
    case 'handoff':
      return { note: '', pause_ai: false };
    case 'repeat':
      return { to: '', times: 1 };
    case 'pick_offer':
      return { kind: 'upsell', found: '', none: 'end' };
    case 'save_answer':
      return { field: 'appointment.not_taken_reason', value: 'price' };
    case 'offer_booking':
      return { service: 'offer' };
    default:
      return {};
  }
}

// ── conditions ───────────────────────────────────────────────────────────────

function ConditionRow({ item, catalog, onChange, onRemove }) {
  const spec = catalog.condition_fields.find((f) => f.field === item.field) || {};
  const ops = Object.entries(catalog.operators).filter(([op]) => {
    if (spec.kind === 'bool') return ['is_true', 'is_false'].includes(op);
    if (spec.kind === 'number') return ['eq', 'ne', 'gt', 'gte', 'lt', 'lte'].includes(op);
    if (spec.kind === 'list') return ['contains', 'not_contains'].includes(op);
    return ['eq', 'ne', 'in', 'not_in', 'contains', 'not_contains'].includes(op);
  });
  const needsValue = !['is_true', 'is_false'].includes(item.op);
  const listValue = ['in', 'not_in'].includes(item.op);
  return (
    <div className="de-row" style={{ gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
      <select className="de-select" style={{ width: 230 }} value={item.field || ''} onChange={(e) => onChange({ field: e.target.value, op: '', value: '' })}>
        <option value="">Choose…</option>
        {catalog.condition_fields.map((f) => (
          <option key={f.field} value={f.field}>
            {f.label}
          </option>
        ))}
      </select>
      <select className="de-select" style={{ width: 150 }} value={item.op || ''} onChange={(e) => onChange({ ...item, op: e.target.value })}>
        <option value="">…</option>
        {ops.map(([op, label]) => (
          <option key={op} value={op}>
            {label}
          </option>
        ))}
      </select>
      {needsValue ? (
        spec.options && !listValue ? (
          <select className="de-select" style={{ width: 160 }} value={item.value ?? ''} onChange={(e) => onChange({ ...item, value: e.target.value })}>
            {spec.options.map((o) => (
              <option key={o} value={o}>
                {o || '(empty)'}
              </option>
            ))}
          </select>
        ) : (
          <input
            className="de-input de-input--bordered"
            style={{ width: 200 }}
            type={spec.kind === 'number' ? 'number' : 'text'}
            value={listValue ? listText(item.value) : item.value ?? ''}
            placeholder={listValue ? 'a, b, c' : 'value'}
            onChange={(e) =>
              onChange({ ...item, value: listValue ? parseList(e.target.value) : spec.kind === 'number' ? Number(e.target.value) : e.target.value })
            }
          />
        )
      ) : null}
      <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={onRemove}>
        ✕
      </button>
    </div>
  );
}

/** A flat "all of / any of" list (nested groups made by the API are kept, shown read-only). */
function ConditionsEditor({ value, catalog, onChange }) {
  const mode = value && value.any ? 'any' : 'all';
  const items = (value && value[mode]) || [];
  const flat = items.filter((i) => i && i.field !== undefined);
  const nested = items.length - flat.length;
  const set = (list) => onChange({ [mode]: [...list, ...items.filter((i) => !(i && i.field !== undefined))] });
  return (
    <div>
      <div className="de-row" style={{ gap: 6, marginBottom: 8, alignItems: 'center' }}>
        <span className="de-small">Customers who match</span>
        <select className="de-select" style={{ width: 90 }} value={mode} onChange={(e) => onChange({ [e.target.value]: items })}>
          <option value="all">all</option>
          <option value="any">any</option>
        </select>
        <span className="de-small">of these {flat.length ? '' : '(none: everyone)'}</span>
      </div>
      {flat.map((item, i) => (
        <ConditionRow
          key={i}
          item={item}
          catalog={catalog}
          onChange={(next) => set(flat.map((x, j) => (j === i ? next : x)))}
          onRemove={() => set(flat.filter((_, j) => j !== i))}
        />
      ))}
      {nested ? <div className="de-small de-muted">+ {nested} grouped condition(s) set through the API</div> : null}
      <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => set([...flat, { field: '', op: '', value: '' }])}>
        + Condition
      </button>
    </div>
  );
}

// ── steps ────────────────────────────────────────────────────────────────────

function StepSelect({ steps, value, onChange, exclude, allowEnd = true, placeholder = 'Next step in the list' }) {
  return (
    <select className="de-select" value={value || ''} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {allowEnd ? <option value="end">End</option> : null}
      {steps
        .filter((s) => s.key !== exclude)
        .map((s) => (
          <option key={s.key} value={s.key}>
            {s.key} · {s.type.replace('_', ' ')}
          </option>
        ))}
    </select>
  );
}

function StepConfig({ step, steps, catalog, templates, templatesAll, onShowAllTemplates, automations, onChange }) {
  const cfg = step.config || {};
  const set = (patch) => onChange({ ...step, config: { ...cfg, ...patch } });
  const row = (label, input) => (
    <label className="de-field">
      <span className="de-label">{label}</span>
      {input}
    </label>
  );
  switch (step.type) {
    case 'wait':
      return (
        <div className="de-form-grid de-form-grid--2">
          {row('Wait', <input className="de-input de-input--bordered" type="number" min={0} value={cfg.amount ?? 0} onChange={(e) => set({ amount: Number(e.target.value) })} />)}
          {row(
            'Unit',
            <select className="de-select" value={cfg.unit || 'minutes'} onChange={(e) => set({ unit: e.target.value })}>
              {UNITS.map((u) => (
                <option key={u} value={u}>
                  {u}
                </option>
              ))}
            </select>,
          )}
          <label className="de-row" style={{ gap: 6, alignItems: 'center', gridColumn: '1 / -1' }}>
            <input type="checkbox" checked={cfg.amount_from === 'offer.delay_days'} onChange={(e) => set({ amount_from: e.target.checked ? 'offer.delay_days' : undefined })} />
            <span className="de-small">Use the offer rule’s own days when it sets them (after a “Pick an offer” step)</span>
          </label>
        </div>
      );
    case 'wait_by_service': {
      const rules = Array.isArray(cfg.rules) ? cfg.rules : [];
      const setRules = (next) => set({ rules: next });
      return (
        <div>
          <div className="de-small de-muted" style={{ marginBottom: 8 }}>
            Services whose name contains the text wait their own number of days (the longest match wins). Every other service waits the default.
          </div>
          {rules.map((rule, i) => (
            <div key={i} className="de-row" style={{ gap: 6, marginBottom: 6, alignItems: 'center', flexWrap: 'wrap' }}>
              <input className="de-input de-input--bordered" style={{ flex: '1 1 140px', minWidth: 0 }} value={rule.match || ''} placeholder="Service name contains, e.g. Nail" onChange={(e) => setRules(rules.map((r, j) => (j === i ? { ...r, match: e.target.value } : r)))} />
              <input className="de-input de-input--bordered" style={{ width: 80 }} type="number" min={1} max={365} value={rule.days ?? ''} onChange={(e) => setRules(rules.map((r, j) => (j === i ? { ...r, days: Number(e.target.value) } : r)))} />
              <span className="de-small">days</span>
              <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setRules(rules.filter((_, j) => j !== i))}>
                ✕
              </button>
            </div>
          ))}
          <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setRules([...rules, { match: '', days: 20 }])}>
            + Service rule
          </button>
          <div className="de-form-grid de-form-grid--2 de-mt-8">
            {row('Every other service: days', <input className="de-input de-input--bordered" type="number" min={1} max={365} value={cfg.default_days ?? 25} onChange={(e) => set({ default_days: Number(e.target.value) })} />)}
          </div>
        </div>
      );
    }
    case 'wait_until':
      return (
        <div className="de-form-grid de-form-grid--2">
          {row(
            'Until',
            <select className="de-select" value={cfg.relative_to === 'appointment' ? 'appointment' : 'time'} onChange={(e) => set(e.target.value === 'appointment' ? { relative_to: 'appointment', time_of_day: undefined } : { relative_to: undefined, time_of_day: '10:00' })}>
              <option value="appointment">A time around the appointment</option>
              <option value="time">The next time of day</option>
            </select>,
          )}
          {cfg.relative_to === 'appointment'
            ? row(
                'Minutes from the appointment (− before, + after)',
                <input className="de-input de-input--bordered" type="number" value={cfg.offset_minutes ?? 0} onChange={(e) => set({ offset_minutes: Number(e.target.value) })} />,
              )
            : row('Time (HH:MM)', <input className="de-input de-input--bordered" type="time" value={cfg.time_of_day || '10:00'} onChange={(e) => set({ time_of_day: e.target.value })} />)}
          {row(
            'If that time has already passed',
            <select className="de-select" value={cfg.if_past || 'skip_next'} onChange={(e) => set({ if_past: e.target.value })}>
              <option value="skip_next">Skip the next step</option>
              <option value="continue">Go on at once</option>
              <option value="stop">Stop the run</option>
            </select>,
          )}
        </div>
      );
    case 'wait_for': {
      const events = Object.entries(cfg.events || {});
      const setEvents = (pairs) => set({ events: Object.fromEntries(pairs.filter(([k]) => k)) });
      return (
        <div>
          {events.map(([ev, target], i) => (
            <div key={i} className="de-row" style={{ gap: 6, marginBottom: 6 }}>
              <select className="de-select" style={{ width: 220 }} value={ev} onChange={(e) => setEvents(events.map((p, j) => (j === i ? [e.target.value, p[1]] : p)))}>
                <option value="button:0">Taps button 1</option>
                <option value="button:1">Taps button 2</option>
                <option value="button:2">Taps button 3</option>
                <option value="replied">Sends any message</option>
                <option value="booked">Books</option>
              </select>
              <span className="de-small" style={{ alignSelf: 'center' }}>
                → go to
              </span>
              <div style={{ width: 230 }}>
                <StepSelect steps={steps} exclude={step.key} value={target} onChange={(v) => setEvents(events.map((p, j) => (j === i ? [p[0], v || 'end'] : p)))} placeholder="Choose…" />
              </div>
              <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setEvents(events.filter((_, j) => j !== i))}>
                ✕
              </button>
            </div>
          ))}
          <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setEvents([...events, [`button:${events.length}`, 'end']])}>
            + Reply path
          </button>
          <div className="de-form-grid de-form-grid--2 de-mt-8">
            {row('No reply after (minutes)', <input className="de-input de-input--bordered" type="number" min={0} value={cfg.timeout_minutes ?? ''} onChange={(e) => set({ timeout_minutes: e.target.value ? Number(e.target.value) : null })} />)}
            {row('Then go to', <StepSelect steps={steps} exclude={step.key} value={cfg.timeout_next} onChange={(v) => set({ timeout_next: v })} />)}
          </div>
        </div>
      );
    }
    case 'send_template': {
      const kinds = Object.fromEntries((catalog.message_kinds || []).map((k) => [k.kind, k]));
      const shown = templates.filter((t) => t.ours || templatesAll || t.name === cfg.template);
      const picked = templates.find((t) => t.name === cfg.template);
      return (
        <div className="de-form-grid de-form-grid--2">
          {cfg.message_kind ? (
            <div className="de-callout" style={{ gridColumn: '1 / -1' }}>
              This step sends <b>{kinds[cfg.message_kind]?.label || cfg.message_kind}</b>, built from the booking (every service, the stylist,
              the branch). The booking’s number, quiet hours and the 24-hour window apply.
            </div>
          ) : null}
          {row(
            'Template',
            <select className="de-select" value={cfg.template || ''} disabled={Boolean(cfg.message_kind)} onChange={(e) => set({ template: e.target.value })}>
              <option value="">Choose a template…</option>
              {shown.map((t) => (
                <option key={t.name} value={t.name}>
                  {t.name} · {TEMPLATE_STATUS[t.status] || t.status}
                </option>
              ))}
            </select>,
          )}
          {!cfg.message_kind ? (
            <label className="de-row" style={{ gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={templatesAll} onChange={(e) => onShowAllTemplates(e.target.checked)} />
              <span className="de-small">Show every template on the WhatsApp account (also other products’)</span>
            </label>
          ) : null}
          {picked && picked.status !== 'approved' ? (
            <div className="de-small de-warn-text" style={{ gridColumn: '1 / -1' }}>
              {picked.name} is {TEMPLATE_STATUS[picked.status] || picked.status}: messages wait until Meta approves it.
            </div>
          ) : null}
          {!cfg.message_kind
            ? row(
                'Language',
                <select className="de-select" value={cfg.language || 'customer'} onChange={(e) => set({ language: e.target.value })}>
                  {LANGUAGE_CHOICES.map(([code, label]) => (
                    <option key={code} value={code}>
                      {label}
                    </option>
                  ))}
                </select>,
              )
            : null}
          {!cfg.message_kind ? (
            <label className="de-row" style={{ gap: 6, alignItems: 'center' }}>
              <input type="checkbox" checked={cfg.quiet_hours ?? false} onChange={(e) => set({ quiet_hours: e.target.checked })} />
              <span className="de-small">Wait until quiet hours end (marketing templates always do)</span>
            </label>
          ) : null}
        </div>
      );
    }
    case 'send_text':
      return (
        <div>
          {row('Message (inside the 24-hour window only; {customer.first_name} style fields work)', <textarea className="de-input de-input--bordered" rows={3} value={cfg.text || ''} onChange={(e) => set({ text: e.target.value })} />)}
          <div className="de-row de-mt-8" style={{ gap: 6 }}>
            {[0, 1, 2].map((i) => (
              <input
                key={i}
                className="de-input de-input--bordered"
                maxLength={20}
                placeholder={`Button ${i + 1} (optional)`}
                value={(cfg.buttons || [])[i] || ''}
                onChange={(e) => {
                  const list = [...(cfg.buttons || ['', '', ''])];
                  list[i] = e.target.value;
                  set({ buttons: list.filter((b, j) => b || j < i) });
                }}
              />
            ))}
          </div>
        </div>
      );
    case 'condition':
      return (
        <div>
          <ConditionsEditor value={cfg.conditions} catalog={catalog} onChange={(conditions) => set({ conditions })} />
          <div className="de-form-grid de-form-grid--2 de-mt-8">
            {row('If yes, go to', <StepSelect steps={steps} exclude={step.key} value={cfg.yes} onChange={(v) => set({ yes: v })} />)}
            {row('If no, go to', <StepSelect steps={steps} exclude={step.key} value={cfg.no} onChange={(v) => set({ no: v })} />)}
          </div>
        </div>
      );
    case 'tag':
      return (
        <div className="de-form-grid de-form-grid--2">
          {row('Add tags', <input className="de-input de-input--bordered" value={listText(cfg.add)} onChange={(e) => set({ add: parseList(e.target.value) })} placeholder="vip, wants_spa" />)}
          {row('Remove tags', <input className="de-input de-input--bordered" value={listText(cfg.remove)} onChange={(e) => set({ remove: parseList(e.target.value) })} />)}
        </div>
      );
    case 'notify_staff':
      return row('Alert text (sent to the branch’s staff alert numbers)', <input className="de-input de-input--bordered" value={cfg.message || ''} onChange={(e) => set({ message: e.target.value })} />);
    case 'handoff':
      return (
        <div>
          {row('Note for the person who takes it', <input className="de-input de-input--bordered" value={cfg.note || ''} onChange={(e) => set({ note: e.target.value })} />)}
          <label className="de-row de-mt-8" style={{ gap: 6, alignItems: 'center' }}>
            <input type="checkbox" checked={Boolean(cfg.pause_ai)} onChange={(e) => set({ pause_ai: e.target.checked })} />
            <span className="de-small">Pause the AI in this customer’s chats</span>
          </label>
        </div>
      );
    case 'stop_automations':
      return row(
        'Stop which of the customer’s other automations (none chosen = all)',
        <select
          className="de-select"
          multiple
          style={{ height: 110 }}
          value={(cfg.automation_ids || []).map(String)}
          onChange={(e) => set({ automation_ids: [...e.target.selectedOptions].map((o) => Number(o.value)) })}
        >
          {automations.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>,
      );
    case 'start_automation':
      return row(
        'Automation to start',
        <select className="de-select" value={cfg.automation_id || ''} onChange={(e) => set({ automation_id: Number(e.target.value) })}>
          <option value="">Choose…</option>
          {automations.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
            </option>
          ))}
        </select>,
      );
    case 'pick_offer':
      return (
        <div className="de-form-grid de-form-grid--2">
          {row(
            'Offer',
            <select className="de-select" value={cfg.kind || 'upsell'} onChange={(e) => set({ kind: e.target.value })}>
              <option value="upsell">Upsell: a service that pairs with the one taken</option>
              <option value="downsell">Downsell: a cheaper alternative</option>
            </select>,
          )}
          <div className="de-small de-muted" style={{ alignSelf: 'end' }}>
            From Offers. The template’s offer fields (service, price, text) show it.
          </div>
          {row('When there is one, go to', <StepSelect steps={steps} exclude={step.key} value={cfg.found} onChange={(v) => set({ found: v })} />)}
          {row('When there is none, go to', <StepSelect steps={steps} exclude={step.key} value={cfg.none} onChange={(v) => set({ none: v })} />)}
        </div>
      );
    case 'save_answer':
      return (
        <div className="de-form-grid de-form-grid--2">
          {row(
            'Save as the reason the service was not taken',
            <select className="de-select" value={cfg.value || 'price'} onChange={(e) => set({ field: 'appointment.not_taken_reason', value: e.target.value })}>
              {Object.entries(REASON_LABEL).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>,
          )}
        </div>
      );
    case 'offer_booking':
      return (
        <div className="de-form-grid de-form-grid--2">
          {row(
            'Book',
            <select className="de-select" value={cfg.service || 'offer'} onChange={(e) => set({ service: e.target.value })}>
              <option value="offer">The offered service</option>
              <option value="booking">The same service again</option>
            </select>,
          )}
          {row(
            'Question when the AI is not answering this chat',
            <input className="de-input de-input--bordered" value={cfg.text || ''} placeholder="Lovely! Which day and time would you like for {service}?" onChange={(e) => set({ text: e.target.value })} />,
          )}
        </div>
      );
    case 'repeat': {
      const earlier = steps.slice(0, steps.findIndex((s) => s.key === step.key));
      return (
        <div className="de-form-grid de-form-grid--2">
          {row('Go back to', <StepSelect steps={earlier} value={cfg.to} allowEnd={false} placeholder="Choose an earlier step…" onChange={(v) => set({ to: v })} />)}
          {row('How many more times', <input className="de-input de-input--bordered" type="number" min={1} max={60} value={cfg.times ?? 1} onChange={(e) => set({ times: Number(e.target.value) })} />)}
        </div>
      );
    }
    default:
      return null;
  }
}

function StepCard({ step, index, steps, catalog, templates, templatesAll, onShowAllTemplates, automations, onChange, onRemove, onMove }) {
  const spec = catalog.steps.find((s) => s.type === step.type) || {};
  const branches = ['condition', 'wait_for', 'end', 'repeat', 'pick_offer'].includes(step.type);
  return (
    <div className="de-card" style={{ marginBottom: 10, padding: 14 }}>
      <div className="de-row" style={{ justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
        <div className="de-td-strong">
          <span style={{ marginRight: 6 }}>{STEP_ICON[step.type] || '•'}</span>
          {index + 1}. {spec.label || step.type}
          <span className="de-small de-muted de-mono" style={{ marginLeft: 8 }}>
            {step.key}
          </span>
        </div>
        <div className="de-row" style={{ gap: 4 }}>
          <button type="button" className="de-btn de-btn--outline de-btn--sm" disabled={index === 0} onClick={() => onMove(-1)} title="Move up">
            ↑
          </button>
          <button type="button" className="de-btn de-btn--outline de-btn--sm" disabled={index === steps.length - 1} onClick={() => onMove(1)} title="Move down">
            ↓
          </button>
          <button type="button" className="de-btn de-btn--outline de-btn--sm" onClick={onRemove}>
            Remove
          </button>
        </div>
      </div>
      <StepConfig
        step={step}
        steps={steps}
        catalog={catalog}
        templates={templates}
        templatesAll={templatesAll}
        onShowAllTemplates={onShowAllTemplates}
        automations={automations}
        onChange={onChange}
      />
      {!branches ? (
        <div className="de-row de-mt-8" style={{ gap: 8, alignItems: 'center' }}>
          <span className="de-small de-muted">Then</span>
          <div style={{ width: 260 }}>
            <StepSelect steps={steps} exclude={step.key} value={step.next} onChange={(v) => onChange({ ...step, next: v || undefined })} />
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ── the editor ───────────────────────────────────────────────────────────────

const STATUS_RANK = { approved: 0, pending: 1, draft: 2, paused: 3, rejected: 4, disabled: 5 };

export default function AutomationEditor({ automationId, catalog, onClose, onChanged }) {
  const { canManage, branches } = useEngagementScope();
  const [templatesAll, setTemplatesAll] = useState(false);
  const [branchChoice, setBranchChoice] = useState(null); // all | only | except, while no branch is picked yet
  const [row, setRow] = useState(null);
  const [definition, setDefinition] = useState(null);
  const [meta, setMeta] = useState({ name: '', folder: '', description: '' });
  const [templates, setTemplates] = useState([]);
  const [automations, setAutomations] = useState([]);
  const [problems, setProblems] = useState([]);
  const [busy, setBusy] = useState('');
  const [testPhone, setTestPhone] = useState('');
  const [testResult, setTestResult] = useState(null);

  useEffect(() => {
    api
      .getAutomation(automationId)
      .then((r) => {
        setRow(r);
        setDefinition(r.definition);
        setMeta({ name: r.name, folder: r.folder, description: r.description });
        setProblems(r.problems || []);
      })
      .catch((e) => toast.error(e.message));
    api
      .listWaTemplates()
      .then((list) => {
        // One entry per name, with its best status on any account. "Ours": made here, from the starter pack, or
        // sent by the starters; imported ones (other products on the same WhatsApp account) only when asked.
        const byName = {};
        list.forEach((t) => {
          const known = byName[t.name] || { name: t.name, status: t.status, ours: false };
          if ((STATUS_RANK[t.status] ?? 9) < (STATUS_RANK[known.status] ?? 9)) known.status = t.status;
          known.ours = known.ours || t.origin !== 'imported' || Boolean(t.required) || Boolean((t.used_by || []).length);
          byName[t.name] = known;
        });
        setTemplates(Object.values(byName).sort((a, b) => a.name.localeCompare(b.name)));
      })
      .catch(() => setTemplates([]));
    api
      .listAutomations()
      .then((list) => setAutomations(list.filter((a) => a.id !== automationId)))
      .catch(() => setAutomations([]));
  }, [automationId]);

  const trig = definition?.trigger || {};
  const trigSpec = useMemo(() => catalog.triggers.find((t) => t.type === trig.type) || {}, [catalog, trig.type]);
  const triggerGroups = useMemo(() => {
    const out = {};
    catalog.triggers.forEach((t) => {
      (out[t.group] = out[t.group] || []).push(t);
    });
    return out;
  }, [catalog]);

  if (!row || !definition) {
    return (
      <Modal title="Automation" onClose={onClose} size="wide">
        <div className="de-loader">
          <Spinner /> Loading…
        </div>
      </Modal>
    );
  }

  const steps = definition.steps || [];
  const settings = definition.settings || {};
  const setDef = (patch) => setDefinition((d) => ({ ...d, ...patch }));
  const setTrigger = (patch) => setDef({ trigger: { ...trig, ...patch } });
  const setFilters = (patch) => setTrigger({ filters: { ...(trig.filters || {}), ...patch } });
  const setSteps = (list) => setDef({ steps: list });
  const setSettings = (patch) => setDef({ settings: { ...settings, ...patch } });
  const onlyBranches = (settings.branches || []).map(String);
  const exceptBranches = (settings.exclude_branches || []).map(String);
  const branchMode = onlyBranches.length ? 'only' : exceptBranches.length ? 'except' : branchChoice || 'all';
  const setBranchMode = (mode) => {
    const picked = onlyBranches.length ? onlyBranches : exceptBranches;
    setBranchChoice(mode);
    if (mode === 'all') setSettings({ branches: [], exclude_branches: [] });
    if (mode === 'only') setSettings({ branches: picked.map(Number), exclude_branches: [] });
    if (mode === 'except') setSettings({ branches: [], exclude_branches: picked.map(Number) });
  };

  const save = async (then) => {
    setBusy(then || 'save');
    try {
      const saved = await api.saveAutomation(row.id, { ...meta, definition });
      setRow(saved);
      setProblems(saved.problems || []);
      if (then === 'publish' || then === 'activate') {
        if ((saved.problems || []).length) throw new Error('Fix the problems first');
        const pub = await api.publishAutomation(row.id, then === 'activate');
        setRow(pub);
        toast.success(then === 'activate' ? 'Published and switched on' : 'Published');
      } else if (then === 'test') {
        const run = await api.testAutomation(row.id, { phone: testPhone, fast: true });
        setTestResult(run);
        toast.success('Test run started: messages go only to that number');
      } else {
        toast.success('Draft saved');
      }
      onChanged();
    } catch (e) {
      if (e.data?.problems) setProblems(Array.isArray(e.data.problems) ? e.data.problems : [String(e.data.problems)]);
      toast.error(e.message);
    } finally {
      setBusy('');
    }
  };

  return (
    <Modal title={row.name} onClose={onClose} size="wide">
      <div className="de-row" style={{ gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <span className={`de-pill de-pill--sm de-pill--${row.status === 'active' ? 'ok' : row.status === 'paused' ? 'warn' : 'info'}`}>{row.status}</span>
        {row.live_version ? <span className="de-small de-muted">Published</span> : <span className="de-small de-muted">Not published yet</span>}
        {row.has_unpublished_changes ? <span className="de-pill de-pill--sm de-pill--warn">Unpublished changes</span> : null}
        {row.is_starter ? <span className="de-pill de-pill--sm de-pill--gold">Starter</span> : null}
      </div>
      {row.is_starter ? (
        <div className="de-small de-muted" style={{ marginBottom: 12 }}>
          A starter: on for every organization from day one. Change anything here; it can be paused but not deleted. Reset (on the list) puts back its
          own steps.
        </div>
      ) : null}

      <div className="de-form-grid de-form-grid--2">
        <label className="de-field">
          <span className="de-label">Name</span>
          <input className="de-input de-input--bordered" value={meta.name} onChange={(e) => setMeta((m) => ({ ...m, name: e.target.value }))} />
        </label>
        <label className="de-field">
          <span className="de-label">Folder</span>
          <input className="de-input de-input--bordered" value={meta.folder} onChange={(e) => setMeta((m) => ({ ...m, folder: e.target.value }))} />
        </label>
        <label className="de-field" style={{ gridColumn: '1 / -1' }}>
          <span className="de-label">What it does</span>
          <input className="de-input de-input--bordered" value={meta.description} onChange={(e) => setMeta((m) => ({ ...m, description: e.target.value }))} />
        </label>
      </div>

      <div className="de-section-label de-mt-16">1 · When it starts</div>
      <div className="de-form-grid de-form-grid--2">
        <label className="de-field">
          <span className="de-label">Trigger</span>
          <select className="de-select" value={trig.type || ''} onChange={(e) => setTrigger({ type: e.target.value, filters: {} })}>
            {Object.entries(triggerGroups).map(([group, items]) => (
              <optgroup key={group} label={group}>
                {items.map((t) => (
                  <option key={t.type} value={t.type}>
                    {t.label}
                  </option>
                ))}
              </optgroup>
            ))}
          </select>
          {trigSpec.help ? <span className="de-small de-muted">{trigSpec.help}</span> : null}
        </label>
        <label className="de-field">
          <span className="de-label">Also starts on</span>
          <select className="de-select" multiple style={{ height: 80 }} value={trig.also || []} onChange={(e) => setTrigger({ also: [...e.target.selectedOptions].map((o) => o.value) })}>
            {catalog.triggers
              .filter((t) => t.type !== trig.type)
              .map((t) => (
                <option key={t.type} value={t.type}>
                  {t.label}
                </option>
              ))}
          </select>
        </label>
        {(trigSpec.filters || []).includes('services') ? (
          <label className="de-field">
            <span className="de-label">Only these services (name contains; empty = all)</span>
            <input className="de-input de-input--bordered" value={listText(trig.filters?.services)} onChange={(e) => setFilters({ services: parseList(e.target.value) })} placeholder="Haircut, Facial" />
          </label>
        ) : null}
        {(trigSpec.filters || []).includes('sources') ? (
          <label className="de-field">
            <span className="de-label">Only from (empty = all)</span>
            <input className="de-input de-input--bordered" value={listText(trig.filters?.sources)} onChange={(e) => setFilters({ sources: parseList(e.target.value) })} placeholder="whatsapp, instagram, voice, walk_in" />
          </label>
        ) : null}
        {(trigSpec.filters || []).includes('keywords') ? (
          <label className="de-field">
            <span className="de-label">Keywords (whole words)</span>
            <input className="de-input de-input--bordered" value={listText(trig.filters?.keywords)} onChange={(e) => setFilters({ keywords: parseList(e.target.value) })} placeholder="offer, price" />
          </label>
        ) : null}
        {(trigSpec.filters || []).includes('button_texts') ? (
          <label className="de-field">
            <span className="de-label">Button texts (empty = any button)</span>
            <input className="de-input de-input--bordered" value={listText(trig.filters?.button_texts)} onChange={(e) => setFilters({ button_texts: parseList(e.target.value) })} />
          </label>
        ) : null}
        {(trigSpec.filters || []).includes('days') ? (
          <label className="de-field">
            <span className="de-label">Days since the last visit</span>
            <input className="de-input de-input--bordered" type="number" min={1} value={trig.filters?.days || ''} onChange={(e) => setFilters({ days: Number(e.target.value) })} />
          </label>
        ) : null}
      </div>

      <div className="de-section-label de-mt-16">2 · Who enters</div>
      <ConditionsEditor value={definition.conditions} catalog={catalog} onChange={(conditions) => setDef({ conditions })} />

      <div className="de-section-label de-mt-16">3 · Branches</div>
      <div className="de-form-grid de-form-grid--2">
        <label className="de-field">
          <span className="de-label">Runs for</span>
          <select className="de-select" value={branchMode} onChange={(e) => setBranchMode(e.target.value)}>
            <option value="all">Every branch</option>
            <option value="only">Only these branches</option>
            <option value="except">Every branch except these</option>
          </select>
        </label>
        {branchMode !== 'all' ? (
          <label className="de-field">
            <span className="de-label">{branchMode === 'only' ? 'Only' : 'Not'}</span>
            <BranchSelect
              multiple
              branches={branches}
              value={branchMode === 'only' ? onlyBranches : exceptBranches}
              onChange={(ids) => setSettings(branchMode === 'only' ? { branches: ids.map(Number), exclude_branches: [] } : { branches: [], exclude_branches: ids.map(Number) })}
              placeholder="Choose branches"
            />
          </label>
        ) : null}
      </div>
      <div className="de-small de-muted" style={{ marginTop: 4 }}>
        The branch of the booking, or of the chat when there is no booking. A customer with no branch yet enters unless “Only these branches” is set.
      </div>

      <div className="de-section-label de-mt-16">4 · Steps</div>
      {steps.map((s, i) => (
        <StepCard
          key={s.key}
          step={s}
          index={i}
          steps={steps}
          catalog={catalog}
          templates={templates}
          templatesAll={templatesAll}
          onShowAllTemplates={setTemplatesAll}
          automations={automations}
          onChange={(next) => setSteps(steps.map((x, j) => (j === i ? next : x)))}
          onRemove={() => setSteps(steps.filter((_, j) => j !== i))}
          onMove={(d) => {
            const list = [...steps];
            [list[i], list[i + d]] = [list[i + d], list[i]];
            setSteps(list);
          }}
        />
      ))}
      <div className="de-row" style={{ gap: 6, flexWrap: 'wrap' }}>
        {catalog.steps.map((t) => (
          <button key={t.type} type="button" className="de-btn de-btn--outline de-btn--sm" onClick={() => setSteps([...steps, { key: newKey(steps, t.type), type: t.type, config: defaultConfig(t.type) }])}>
            {STEP_ICON[t.type] || '+'} {t.label}
          </button>
        ))}
      </div>

      <div className="de-section-label de-mt-16">5 · Stops when</div>
      <div className="de-row" style={{ gap: 14, flexWrap: 'wrap' }}>
        {Object.entries(catalog.stop_events).map(([key, label]) => (
          <label key={key} className="de-row" style={{ gap: 6, alignItems: 'center' }}>
            <input
              type="checkbox"
              checked={(definition.stop_on || []).includes(key)}
              onChange={(e) => setDef({ stop_on: e.target.checked ? [...(definition.stop_on || []), key] : (definition.stop_on || []).filter((k) => k !== key) })}
            />
            <span className="de-small">{label}</span>
          </label>
        ))}
      </div>

      <div className="de-section-label de-mt-16">6 · Entering again</div>
      <div className="de-form-grid de-form-grid--2">
        <label className="de-field">
          <span className="de-label">A customer can enter</span>
          <select className="de-select" value={settings.re_entry || 'once_per_booking'} onChange={(e) => setSettings({ re_entry: e.target.value })}>
            {Object.entries(catalog.re_entry).map(([key, label]) => (
              <option key={key} value={key}>
                {label}
              </option>
            ))}
          </select>
        </label>
        {settings.re_entry === 'after_days' ? (
          <label className="de-field">
            <span className="de-label">Days</span>
            <input className="de-input de-input--bordered" type="number" min={0} step="0.5" value={settings.re_entry_days || 0} onChange={(e) => setSettings({ re_entry_days: Number(e.target.value) })} />
          </label>
        ) : null}
      </div>

      {problems.length ? (
        <div className="de-callout de-mt-16" style={{ borderColor: 'var(--de-danger)' }}>
          <div className="de-td-strong">To fix before publishing:</div>
          <ul style={{ margin: '6px 0 0 18px' }}>
            {problems.map((p) => (
              <li key={p} className="de-small">
                {p}
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {(row.versions || []).length ? (
        <>
          <div className="de-section-label de-mt-16">Versions</div>
          <div className="de-small" style={{ display: 'grid', gap: 2 }}>
            {row.versions.slice(0, 8).map((v) => (
              <div key={v.id} className={v.version === row.live_version ? 'de-strong' : 'de-muted'}>
                v{v.version} · published {new Date(v.published_at).toLocaleString()}
                {v.published_by ? ` by ${v.published_by}` : ''}
                {v.version === row.live_version ? ' · live' : ''}
              </div>
            ))}
            <div className="de-muted">Customers already in it finish on the version they started with.</div>
          </div>
        </>
      ) : null}

      {canManage ? <div className="de-section-label de-mt-16">Test it</div> : null}
      {canManage ? (
      <div className="de-row" style={{ gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <input className="de-input de-input--bordered" style={{ width: 220 }} value={testPhone} onChange={(e) => setTestPhone(e.target.value)} placeholder="Test mobile number" />
        <button type="button" className="de-btn de-btn--outline" disabled={testPhone.replace(/\D/g, '').length < 10 || Boolean(busy)} onClick={() => save('test')}>
          {busy === 'test' ? <Spinner /> : null}
          Run the draft now (waits shortened)
        </button>
      </div>
      ) : null}
      {testResult ? (
        <div className="de-small de-mt-8">
          Test run #{testResult.id}: {testResult.status}
          {(testResult.events || []).slice(-4).map((e, i) => (
            <div key={i} className="de-muted">
              {e.step} · {e.event} {e.detail}
            </div>
          ))}
        </div>
      ) : null}

      <div className="de-form-actions" style={{ marginTop: 18 }}>
        <button type="button" className="de-btn de-btn--outline" onClick={onClose}>
          Close
        </button>
        {canManage ? (
          <button type="button" className="de-btn de-btn--outline" disabled={Boolean(busy)} onClick={() => save()}>
            {busy === 'save' ? <Spinner /> : null}
            Save draft
          </button>
        ) : null}
        {canManage ? (
          <button type="button" className="de-btn de-btn--outline" disabled={Boolean(busy)} onClick={() => save('publish')}>
            {busy === 'publish' ? <Spinner /> : null}
            Publish
          </button>
        ) : null}
        {canManage && row.status !== 'active' ? (
          <button type="button" className="de-btn de-btn--primary" disabled={Boolean(busy)} onClick={() => save('activate')}>
            {busy === 'activate' ? <Spinner light /> : null}
            Publish and switch on
          </button>
        ) : null}
      </div>
    </Modal>
  );
}
